import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import numbro from 'numbro';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { SectionHeading } from '../../PersonalInfo.styles';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { FormValues } from '../../PersonalInfo.meta';
import { Separator } from '../../../../CommonComponents/OPL/Separator/Separator';
import { HelperText } from '../../../../CommonComponents/OPL/HelperText/HelperText';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';

interface Props {
  showPartnerSection: boolean;
  principalLifetimeGiftingExemptionAmount: number;
  spouseLifetimeGiftingExemptionAmount?: number;
  addUnusedExemption: boolean;
}

export const Deductions = ({
  showPartnerSection,
  principalLifetimeGiftingExemptionAmount,
  spouseLifetimeGiftingExemptionAmount,
  addUnusedExemption,
}: Props) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<FormValues>();

  const [
    fillingStatus,
    partnerFirstName,
    partnerDob,
    principalLifetimeGiftingExemptionUsedAmount,
    spouseLifetimeGiftingExemptionUsedAmount,
  ] = watch([
    'personalInfo.filingStatus',
    'personalInfo.partner.firstName',
    'personalInfo.partner.dob',
    'deductionsAndGifts.principal.lifetimeGiftingExemptionUsedAmount',
    'deductionsAndGifts.spouse.lifetimeGiftingExemptionUsedAmount',
  ]);

  const hasSpouseInfo = partnerFirstName && partnerDob;

  const principalRemainingAmount = Math.max(
    0,
    numbro(principalLifetimeGiftingExemptionAmount)
      .subtract(principalLifetimeGiftingExemptionUsedAmount || 0)
      .value() || 0,
  );

  return (
    <>
      <section>
        <SectionHeading as="h2">Your Deductions</SectionHeading>

        <Box display="flex" flexDirection="column" gridGap="32">
          <Controller
            control={control}
            name="deductionsAndGifts.principal.administrationExpenseDeductionAmount"
            render={({ field }) => (
              <InputNumberMask
                {...field}
                label="Estate Administration Expense Deduction"
                leftIcon="fa fa-dollar-sign"
                allowNegative={false}
                formVariant={
                  errors.deductionsAndGifts?.principal?.administrationExpenseDeductionAmount
                    ? 'error'
                    : undefined
                }
                helperText={
                  errors.deductionsAndGifts?.principal?.administrationExpenseDeductionAmount
                    ?.message
                }
              />
            )}
          />

          <div>
            <Controller
              control={control}
              name="deductionsAndGifts.principal.lifetimeGiftingExemptionUsedAmount"
              render={({ field: { value, onChange, ...field } }) => (
                <InputNumberMask
                  {...field}
                  value={value}
                  label="Lifetime Gifting Exemption Amount Used"
                  leftIcon="fa fa-dollar-sign"
                  allowNegative={false}
                  onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                  formVariant={
                    errors.deductionsAndGifts?.principal?.lifetimeGiftingExemptionUsedAmount
                      ? 'error'
                      : undefined
                  }
                  helperText={
                    errors.deductionsAndGifts?.principal?.lifetimeGiftingExemptionUsedAmount
                      ?.message
                  }
                />
              )}
            />
            <HelperText mt="4">
              Remaining amount:{' '}
              {formatCurrency(principalRemainingAmount, {
                decimalPlaces: 0,
              })}
            </HelperText>
          </div>
        </Box>
      </section>

      {showPartnerSection && fillingStatus > 1 && hasSpouseInfo && (
        <>
          <Separator my="32" />

          <section>
            <SectionHeading as="h2">Your Partner</SectionHeading>

            <Box display="flex" flexDirection="column" gridGap="32">
              <Controller
                control={control}
                name="deductionsAndGifts.spouse.administrationExpenseDeductionAmount"
                render={({ field }) => (
                  <InputNumberMask
                    {...field}
                    label="Estate Administration Expense Deduction"
                    placeholder="Estate Administration Expense Deduction"
                    leftIcon="fa fa-dollar-sign"
                    allowNegative={false}
                    formVariant={
                      errors.deductionsAndGifts?.spouse?.administrationExpenseDeductionAmount
                        ? 'error'
                        : undefined
                    }
                    helperText={
                      errors.deductionsAndGifts?.spouse?.administrationExpenseDeductionAmount
                        ?.message
                    }
                  />
                )}
              />

              {spouseLifetimeGiftingExemptionAmount !== undefined && (
                <Box>
                  <Controller
                    control={control}
                    name="deductionsAndGifts.spouse.lifetimeGiftingExemptionUsedAmount"
                    render={({ field: { value, onChange, ...field } }) => (
                      <InputNumberMask
                        {...field}
                        value={value}
                        label="Lifetime Gifting Exemption Amount Used"
                        placeholder="Lifetime Gifting Exemption Amount Used"
                        leftIcon="fa fa-dollar-sign"
                        allowNegative={false}
                        onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                        formVariant={
                          errors.deductionsAndGifts?.spouse?.lifetimeGiftingExemptionUsedAmount
                            ? 'error'
                            : undefined
                        }
                        helperText={
                          errors.deductionsAndGifts?.spouse?.lifetimeGiftingExemptionUsedAmount
                            ?.message
                        }
                      />
                    )}
                  />
                  <HelperText mt="4">
                    Remaining amount:{' '}
                    {formatCurrency(
                      Math.max(
                        0,
                        numbro(spouseLifetimeGiftingExemptionAmount)
                          .add(addUnusedExemption ? principalRemainingAmount : 0)
                          .subtract(spouseLifetimeGiftingExemptionUsedAmount || 0)
                          .value() || 0,
                      ),
                      {
                        decimalPlaces: 0,
                      },
                    )}
                  </HelperText>
                </Box>
              )}
            </Box>
          </section>
        </>
      )}
    </>
  );
};
